import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createSetting, fetchSetting } from "./thunks";
import { toast } from "react-toastify";

interface PaginatedSetting {
  data: any[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface SettingState {
  setting: PaginatedSetting;
  loading: boolean;
  error: string | null;
}

const initialState: SettingState = {
  setting: { data: [] },
  loading: false,
  error: null,
};

const SettingSlice = createSlice({
  name: "Setting",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSetting.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const payload = action.payload;

        // Possible shapes:
        // 1. Single object (payload.id)
        // 2. Wrapped single object (payload.data.id)
        // 3. Array (payload.data is array)
        // 4. Paginated (payload.data.data is array)

        if (payload) {
          if (Array.isArray(payload.data)) {
            // Raw array of settings
            state.setting = { data: payload.data };
          } else if (payload.data && Array.isArray(payload.data.data)) {
            // Paginated style
            state.setting = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          } else if (payload.id) {
            // Single object root-level
            state.setting = { data: [payload] };
          } else if (payload.data && payload.data.id) {
            // Single object nested under data
            state.setting = { data: [payload.data] };
          } else {
            // Fallback: keep existing or reset
            state.setting = { data: [] };
          }
        }
      })
      .addCase(fetchSetting.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Create
      .addCase(createSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSetting.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const payload = action.payload;
        const newObj = payload?.data?.id
          ? payload.data
          : payload.id
            ? payload
            : payload.data;
        if (newObj) {
          // Replace existing (upsert semantics) keeping only one current setting
          state.setting = { data: [newObj] };
        }
        toast.success("Configurações salvas com sucesso!");
      })
      .addCase(createSetting.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          "Não foi possível salvar as configurações, tente novamente!",
        );
      });
  },
});

export default SettingSlice.reducer;
